<template>
    <div class="eureka-ip-header-container-right-transition">
        <button
            class="eureka-ip-header-language-trigger"
            @click.stop="showMenuDropdown = !showMenuDropdown"
        >
            <span>{{ currentMenuLabel }}</span>
            <PtIcon
                icon="SolidArrowsmallDown"
                :class="['eureka-ip-header-language-icon', { 'eureka-ip-header-language-icon-open': showMenuDropdown }]"
            />
        </button>

        <!-- Menu Dropdown -->
        <div
            v-if="showMenuDropdown"
            class="eureka-ip-header-language-dropdown"
            @click.stop
        >
            <button
                v-for="menu in menuItems"
                :key="menu.path"
                :class="[
                    'eureka-ip-header-language-item',
                    { 'eureka-ip-header-language-item-active': isActive(menu.path) }
                ]"
                @click="navigateToMenu(menu.path)"
            >
                <span>{{ menu.label }}</span>
                <PtIcon
                    v-if="isActive(menu.path)"
                    icon="SolidTick"
                    class="eureka-ip-header-language-check"
                />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { PtIcon } from '@patsnap-biz/icon';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const showMenuDropdown = ref(false);

// 菜单项配置
const menuItems = [
    {
        path: '/patent-drafting/projects',
        label: t('nav.draftingCenter'),
        key: 'draftingCenter',
    },
    {
        path: '/templates',
        label: t('nav.templates'),
        key: 'templates',
    },
    {
        path: '/instructions',
        label: t('nav.instructions'),
        key: 'instructions',
    },
    {
        path: '/writing-styles',
        label: t('nav.styles'),
        key: 'styles',
    },
];

// 当前选中的菜单标签
const currentMenuLabel = computed(() => {
    const currentMenu = menuItems.find(menu => isActive(menu.path));
    return currentMenu ? currentMenu.label : menuItems[0].label;
});

// 判断路由是否激活
const isActive = (path: string) => {
    return route.path === path || route.path.startsWith(path);
};

// 导航到指定菜单
const navigateToMenu = (path: string) => {
    router.push(path);
    showMenuDropdown.value = false;
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event: Event) => {
    const target = event.target as Element;
    if (!target.closest('.eureka-ip-header-container-right-transition')) {
        showMenuDropdown.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
.eureka-ip-header-container-right-transition {
    position: relative;
}

.eureka-ip-header-language {
    &-trigger {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px 12px;
        color: #020A1A;
        cursor: pointer;
        font-size: 14px;
        border: none;
        background: transparent;
        transition: color 0.2s;

        &:hover {
            color: #020A1A;
            background-color: #B3BAC533;
            border-radius: 4px;
        }
    }

    &-icon {
        width: 14px;
        height: 14px;
        fill: #495973;
        transition: transform 0.2s;

        &-open {
            transform: rotate(180deg);
        }
    }

    &-dropdown {
        position: absolute;
        top: 48px;
        right: 0;
        width: 180px;
        background: #fff;
        border-radius: 4px;
        border: 1px solid #DFE1E6;
        padding: 8px;
        z-index: 991;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &-item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 4px;
        justify-content: space-between;
        padding: 6px 8px;
        font-size: 14px;
        color: #020A1A;
        background: transparent;
        border: none;
        cursor: pointer;
        text-align: left;
        transition: background-color 0.2s;
        border-radius: 4px;

        &:hover {
            background-color: #B3BAC533;
        }

        &-active {
            background-color: transparent;
            color: #0764E9;
        }
    }

    &-check {
        width: 24px;
        height: 24px;
        fill: #0764E9;
    }
}
</style>
